/**
 * Route guards for the affiliate portal.
 *
 * The affiliate session is independent of the member session, so these cannot
 * reuse `ProtectedRoute` / `AnonymousRoute` — a signed-in member is still
 * anonymous to the portal, and vice versa. Both gate on `initializing` so a
 * stored session is read back before a decision is made.
 */
import type { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useAffiliateSession } from '@wlb/shared/context/AffiliateSessionContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { AFFILIATE_ROUTES } from './affiliateRoutes';

function SessionBootstrapGate() {
  const { t } = useTranslation();
  return (
    <div className="app-loader" role="status">
      {t('common.loading')}
    </div>
  );
}

/** Sends visitors without an affiliate session to the portal sign-in. */
export function AffiliateProtectedRoute({ children }: { children: ReactElement }) {
  const { initializing, isSignedIn } = useAffiliateSession();

  if (initializing) return <SessionBootstrapGate />;
  return isSignedIn ? children : <Navigate to={AFFILIATE_ROUTES.SIGN_IN} replace />;
}

/** Keeps a signed-in affiliate out of the portal sign-in / application screens. */
export function AffiliateAnonymousRoute({ children }: { children: ReactElement }) {
  const { initializing, isSignedIn } = useAffiliateSession();

  if (initializing) return <SessionBootstrapGate />;
  return isSignedIn ? <Navigate to={AFFILIATE_ROUTES.DASHBOARD} replace /> : children;
}
